// ProtectedRoute.js
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const orderedRoutes = [
  { path: '/', permission: 'read:dashboard' },
  { path: '/page1', permission: 'manage:orders' },
  { path: '/orderlist', permission: 'read:orders' },
  { path: '/page2', permission: 'manage:partners' },
  { path: '/brands', permission: 'manage:brands' },
  { path: '/locations', permission: 'manage:locations' },
  { path: '/add-partner', permission: 'create:partners' },
  { path: '/partner-details', permission: 'manage:partners' },
  { path: '/assets', permission: 'manage:assets' },
  { path: '/BillingManagement', permission: 'manage:billing' },
  { path: '/applicants', permission: 'manage:applicants' },
  { path: '/applicants/add', permission: 'create:applicants' },
  { path: '/applicant-details', permission: 'manage:applicant-details' },
  { path: '/user-management', permission: 'manage:users' },
  { path: '/settings', permission: 'manage:settings' }
];

const routePermissions = {
  '/': 'read:dashboard',
  '/page1': 'manage:orders',
  '/orderlist': 'read:orders',
  '/page2': 'manage:partners',
  '/brands': 'manage:brands',
  '/locations': 'manage:locations',
  '/add-partner': 'create:partners',
  '/partner-details': 'manage:partners',
  '/assets': 'manage:assets',
  '/BillingManagement': 'manage:billing',
  '/applicants': 'manage:applicants',
  '/applicants/add': 'create:applicants',
  '/applicant-details': 'manage:applicant-details',
  '/user-management': 'manage:users',
  '/settings': 'manage:settings'
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [hasPermission, setHasPermission] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        if (!isAuthenticated) {
          setHasPermission(false);
          setIsChecking(false);
          return;
        }

        const token = await getAccessTokenSilently();
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const userPermissions = decodedToken.permissions || [];

        // Wenn wir auf der Homepage sind, prüfen wir auf Dashboard-Permission
        if (location.pathname === '/') {
          if (!userPermissions.includes('read:dashboard')) {
            // Finde die erste verfügbare Route
            const firstAvailableRoute = orderedRoutes.find(route => 
              userPermissions.includes(route.permission)
            );

            if (firstAvailableRoute) {
              navigate(firstAvailableRoute.path);
              return;
            } else {
              navigate('/unauthorized');
              return;
            }
          }
        }

        // Finde die erforderliche Permission für die aktuelle Route
        const path = location.pathname;
        let requiredPermission;

        // Erst exakte Route prüfen
        if (routePermissions[path]) {
          requiredPermission = routePermissions[path];
        } else {
          // Wenn keine exakte Route gefunden, nach Base-Path suchen
          // Sortiere die Routen nach Länge (längste zuerst), um spezifischere Matches zuerst zu prüfen
          const basePath = Object.keys(routePermissions)
            .sort((a, b) => b.length - a.length)
            .find(route => 
              path.startsWith(route) && route !== '/'  // Root-Pfad von partiellen Matches ausschließen
            );
          requiredPermission = basePath ? routePermissions[basePath] : null;
        }

        console.log('Permission Check:', {
          path,
          requiredPermission,
          userPermissions,
          hasPermission: requiredPermission ? userPermissions.includes(requiredPermission) : true
        });

        if (!requiredPermission) {
          setHasPermission(true);
          setIsChecking(false);
          return;
        }

        setHasPermission(userPermissions.includes(requiredPermission));
        setIsChecking(false);
      } catch (error) {
        console.error('Permission check failed:', error);
        setHasPermission(false);
        setIsChecking(false);
      }
    };

    checkPermissions();
  }, [isAuthenticated, getAccessTokenSilently, location, navigate]);

  useEffect(() => {
    // Logging für Debugging
    console.log('Route Protection Status:', {
      path: location.pathname,
      isChecking,
      hasPermission,
      isAuthenticated
    });
  }, [location.pathname, isChecking, hasPermission, isAuthenticated]);

  if (isLoading || isChecking) {
    return <div>Lädt...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!hasPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;